import { Box, SxProps } from '@mui/material';
import { GridCellParams, GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';

import { useAuth } from '@verantos-dev/core-auth';
import { OrganizationsDetails } from '@verantos-dev/data-access-organizations';
import { ActionMenu, ActionMenuItemProps, StatusDisplay } from '@verantos-dev/shared-components';
import { stringCellStyles } from '@verantos-dev/shared-styles';
import { formatDate } from '@verantos-dev/shared-utils';

import { OrganizationType, getSubTypeLabel } from './useOrganizationsListCreateEditDialog';
import {
  DialogActionType,
  useOrganizationsListDialogContext,
} from '../context/OrganizationsListDialogContext';

const headerEntryStyles: SxProps = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  lineHeight: 'normal',
  padding: '16px 24px',
  fontWeight: '500',
  fontSize: '14px',
  color: '#252A36',
};

export const useOrganizationsListTable = () => {
  const intl = useIntl();
  const { dispatch } = useOrganizationsListDialogContext();
  const {
    user: { isAdmin },
  } = useAuth();

  // the column headers for the organizations table
  const organizationsTableColumns: GridColDef[] = [
    {
      headerName: intl.formatMessage({ id: 'organizations.tableHeader.name' }),
      field: 'name',
      type: 'string',
      minWidth: 306,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const organizationId = params?.row.organizationId;
        return (
          <Box data-testid={`organization-name-${organizationId}`} sx={stringCellStyles}>
            {params.row.displayName}
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box
          data-testid={`organization-name-list-header-${params.colDef.field}`}
          sx={headerEntryStyles}
        >
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({ id: 'organizations.tableHeader.status' }),
      field: 'status',
      type: 'custom',
      minWidth: 230,
      maxWidth: 230,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const organizationId = params?.row.organizationId;
        return (
          <Box data-testid={`organization-status-${organizationId}`} sx={stringCellStyles}>
            <StatusDisplay id={`organization-status-${organizationId}`} status={params.value} />
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box
          data-testid={`organization-status-list-header-${params.colDef.field}`}
          sx={headerEntryStyles}
        >
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({ id: 'organizations.tableHeader.type' }),
      field: 'type',
      type: 'string',
      minWidth: 230,
      maxWidth: 230,
      flex: 2,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams<OrganizationsDetails>) => {
        const organizationId = params?.row.organizationId;
        const dataType = OrganizationType[params.row.type as keyof typeof OrganizationType];
        const type = dataType || params.row.type;
        return (
          <Box data-testid={`organization-type-${organizationId}`} sx={stringCellStyles}>
            {type}
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box
          data-testid={`organization-type-list-header-${params.colDef.field}`}
          sx={headerEntryStyles}
        >
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({ id: 'organizations.tableHeader.subType' }),
      field: 'subType',
      type: 'string',
      minWidth: 130,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams<OrganizationsDetails>) => {
        const organizationId = params?.row.organizationId;
        const subType = getSubTypeLabel(params.row.subType);
        return (
          <Box data-testid={`organization-subtype-${organizationId}`} sx={stringCellStyles}>
            {subType}
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box
          data-testid={`organization-subtype-list-header-${params.colDef.field}`}
          sx={headerEntryStyles}
        >
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({ id: 'organizations.tableHeader.creationDate' }),
      field: 'creationDate',
      type: 'string',
      minWidth: 130,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const organizationId = params?.row.organizationId;
        return (
          <Box
            data-testid={`organization-creation-date-organization-${organizationId}`}
            sx={stringCellStyles}
          >
            {formatDate(params.row.createdBy.transactionDate, 'dateShort')}
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box
          data-testid={`organization-creation-date-list-header-${params.colDef.field}`}
          sx={headerEntryStyles}
        >
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({
        id: 'common.grid.columnName.actions',
        defaultMessage: 'Actions',
      }),
      field: '',
      type: 'actions',
      minWidth: 70,
      maxWidth: 70,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams<OrganizationsDetails>) => {
        const actionMenu: ActionMenuItemProps[] = [
          {
            id: 'editOrganization',
            label: 'Edit organization',
            disabled:
              !isAdmin || params.row.type.toLowerCase() === OrganizationType.VERANTOS.toLowerCase(),
            onClick: () => {
              dispatch({
                type: DialogActionType.OPEN_DIALOG,
                payload: 'editOrganization',
                data: params.row,
              });
            },
            icon: 'edit',
          },
          ...(isAdmin && params.row.type.toLowerCase() !== OrganizationType.VERANTOS.toLowerCase()
            ? [
                {
                  id: 'assign-organization',
                  label: 'Manage data sets',
                  onClick: () => {
                    dispatch({
                      type: DialogActionType.OPEN_DIALOG,
                      payload: 'selectDatasets',
                      data: params?.row,
                    });
                  },
                  icon: 'organizationDatasetAssignment',
                },
              ]
            : []),
        ];

        return <ActionMenu id={'organizationList-actionMenu'} menuItems={actionMenu} />;
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box
          data-testid={`organization-action-date-list-header-${params.colDef.field}`}
          sx={headerEntryStyles}
        ></Box>
      ),
    },
  ];

  return {
    organizationsTableColumns,
  };
};
